import { Component, Input, Output, EventEmitter, ViewEncapsulation } from '@angular/core';
import { SpaContext } from '../Classes/spacontext';
import { BreadcrumbStepClass } from '../Classes/BreadcrumbStepClass';
import { UnitModel } from '../Services/api/v2/Models/UnitModel';

@Component({
	standalone: false,
	selector: 'unit-picker',
	template: `
		<breadcrumbs *ngIf="crumbs.length > 1" [steps]="crumbs" [maxCrumbs]="2"></breadcrumbs>
		<div class="header">
			<ng-content></ng-content>
			<div class="searchRow">
				<visiba-input
					icon="Search"
					placeholder="LicenceHolder.Component.UnitPicker.Placeholder.SearchUnit"
					[(ngModel)]="searchStringModel"
					(ngModelChange)="filterUnits()"
				></visiba-input>
			</div>
		</div>
		<ng-container *ngFor="let u of units">
			<div class="unit" (click)="setActiveUnit(u)">
				{{ u.Name }}
				<visiba-svg a="ArrowRight"></visiba-svg>
			</div>
		</ng-container>
		<ng-container *ngFor="let o of offers">
			<div class="unit -offer" (click)="setActiveUnit(o)">
				<visiba-svg a="House"></visiba-svg>
				{{ o.Name }}
			</div>
		</ng-container>
		<ng-container *ngIf="units.length === 0 && offers.length === 0">
			<div class="unit">
				{{ 'Licenceholder.Units.NothingFound' | translate }}
			</div>
		</ng-container>
	`,
	styleUrls: ['../Css/Components/unit-picker.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class UnitPickerComponent {
	@Output()
	public onEntranceSelected = new EventEmitter<{ entranceId: number }>();

	@Input()
	public selectedUnit: number = null;

	public activeUnit: UnitModel = null;

	public offers: UnitModel[] = null;
	public units: UnitModel[] = null;

	public crumbs: Array<BreadcrumbStepClass> = [];
	public searchStringModel: string = null;

	constructor(public _spaContext: SpaContext) {}

	ngOnInit() {
		const entrances = this._spaContext.unitTreeFlat.filter((u) => u.IsClientEntrance && u.IsListedToClients);
		if (entrances.length === 1) {
			this.setActiveUnit(entrances[0]);
		} else {
			const targetUnitId = this.selectedUnit || this._spaContext.unitConfig.LicenceHolder.RootUnit;
			this.setActiveUnit(this._spaContext.unitTreeFlat.find((u) => u.Id === targetUnitId));
		}
	}

	public setActiveUnit(unit: UnitModel) {
		if (typeof unit === 'undefined') return;
		this.searchStringModel = '';

		if (unit.IsClientEntrance) {
			this.onEntranceSelected.emit({ entranceId: unit.Id });
			return;
		}
		this.activeUnit = unit;
		this.crumbs = [];
		this.addCrumb(this.activeUnit);
		this.filterUnits();
	}

	public filterUnits() {
		this.offers = [];
		this.units = [];

		// sorts lists into alphabetical order
		const compare = (unitA: UnitModel, unitB: UnitModel) => {
			const textA = (unitA.Name ?? '').toLowerCase();
			const textB = (unitB.Name ?? '').toLowerCase();

			return textA < textB ? -1 : textA > textB ? 1 : 0;
		};

		if (this.searchStringModel) {
			const recursiveSearchAndAdd = (children: UnitModel[]) => {
				if (children.length) {
					children.forEach((child) => {
						if (child.Children.length) {
							recursiveSearchAndAdd(child.Children);
						}

						if (!(child.Name ?? '').toLowerCase().includes(this.searchStringModel.toLowerCase())) return;

						if (child.IsClientEntrance && child.IsListedToClients) {
							this.offers.push(child);
						}
						if (!child.IsClientEntrance && this.listableIsVisiable(child.Children)) {
							this.units.push(child);
						}
					});
				}
			};

			recursiveSearchAndAdd(this.activeUnit.Children);
			this.offers.sort(compare);
			this.units.sort(compare);
			return;
		}

		this.activeUnit.Children.forEach((child) => {
			const { IsListedToClients, IsClientEntrance, Name, Children } = child;

			if (IsClientEntrance && IsListedToClients) {
				this.offers.push(child);
			} else if (!IsClientEntrance && this.listableIsVisiable(child.Children)) {
				this.units.push(child);
			}
		});

		this.offers.sort(compare);
		this.units.sort(compare);
	}

	public back(parentId: number | null, crumbIndex: number) {
		this.setActiveUnit(this._spaContext.unitTreeFlat.find((u) => u.Id === parentId));
		this.crumbs.splice(crumbIndex + 1);
	}

	private addCrumb(unit: UnitModel) {
		this.crumbs.unshift(
			new BreadcrumbStepClass(unit.Name, null, (step, index) => {
				this.back(unit.Id, index);
			}),
		);

		if (unit.ParentId === null) return;
		const parent = this._spaContext.unitTreeFlat.find((u) => u.Id === unit.ParentId);
		this.addCrumb(parent);
	}

	private listableIsVisiable(children: UnitModel[]): boolean {
		return !!children.find((child) => {
			if (child.IsListedToClients) return true;

			return this.listableIsVisiable(child.Children);
		});
	}
}
